import { fetchConfig } from "@theme/utilities";
import { CartAddEvent, CartErrorEvent } from "@theme/events";

/**
 * WLD product cards: pick a size when shown, then add; single-variant cards use data-variant-id on the button.
 */

function collectCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll("cart-items-component").forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * True when size chips act as links to the PDP instead of in-card selectors.
 * Mirrors the CSS breakpoint in `wld-product-card-styles.liquid`.
 *
 * @returns {boolean}
 */
function chipsNavigateToProduct() {
  return window.matchMedia("(hover: none), (max-width: 640px)").matches;
}

/**
 * @param {HTMLButtonElement} chip
 */
function selectSizeChip(chip) {
  const card = chip.closest(".wld-prod-card");
  if (!card) return;

  card
    .querySelectorAll("button.wld-prod-card__sz[data-variant-id]")
    .forEach((b) => {
      const on = b === chip;
      b.classList.toggle("wld-prod-card__sz--selected", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });
}

/**
 * @param {HTMLElement} sizesWrap
 */
function pulseSizeHint(sizesWrap) {
  sizesWrap.classList.add("wld-prod-card__sizes--pulse");
  window.setTimeout(
    () => sizesWrap.classList.remove("wld-prod-card__sizes--pulse"),
    800,
  );
}

/**
 * @param {HTMLButtonElement} addBtn
 */
async function submitAddToCart(addBtn) {
  const card = addBtn.closest(".wld-prod-card");
  const sizesWrap = card?.querySelector(".wld-prod-card__sizes");
  const selected = card?.querySelector(
    "button.wld-prod-card__sz--selected[data-variant-id]",
  );
  const variantId = addBtn.dataset.variantId || selected?.dataset.variantId;
  const productId =
    addBtn.dataset.productId ?? selected?.dataset.productId ?? "";

  if (!variantId) {
    if (sizesWrap) pulseSizeHint(sizesWrap);
    return;
  }

  addBtn.disabled = true;
  addBtn.setAttribute("aria-busy", "true");

  try {
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");

    const sectionIds = collectCartSectionIds();
    if (sectionIds.length) {
      formData.append("sections", sectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    });

    const data = await response.json();

    if (data.status) {
      document.dispatchEvent(
        new CartErrorEvent(
          "wld-product-card-size",
          data.message,
          data.description,
          data.errors,
        ),
      );
      return;
    }

    /** @type {Record<string, unknown> | null} */
    let cart = null;
    try {
      const cartUrl = `${Theme.routes.cart_url}.js?t=${Date.now()}`;
      const cartRes = await fetch(cartUrl, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (cartRes.ok) {
        cart = await cartRes.json();
      }
    } catch (_) {
      // fall through with cart = null
    }

    const total =
      cart && typeof cart.item_count === "number" ? cart.item_count : 1;

    document.dispatchEvent(
      new CartAddEvent(cart || {}, variantId, {
        source: "wld-product-card",
        itemCount: total,
        productId,
        sections: data.sections,
        variantId,
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    addBtn.disabled = false;
    addBtn.removeAttribute("aria-busy");
  }
}

/**
 * @param {MouseEvent} event
 */
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const addBtn = target.closest("button.wld-prod-card__add-cart");
  if (addBtn) {
    event.preventDefault();
    void submitAddToCart(/** @type {HTMLButtonElement} */ (addBtn));
    return;
  }

  const chip = target.closest("button.wld-prod-card__sz[data-variant-id]");
  if (!chip || chip.classList.contains("wld-prod-card__sz--out")) return;

  event.preventDefault();

  const variantUrl = /** @type {HTMLElement} */ (chip).dataset.variantUrl;
  if (variantUrl && chipsNavigateToProduct()) {
    window.location.href = variantUrl;
    return;
  }

  selectSizeChip(/** @type {HTMLButtonElement} */ (chip));
}

document.addEventListener("click", onDocumentClick);
